import styled from "styled-components";
import {CREATE_CARD_BLOCK_PADDING, EMPTY_CREATE_CARD_PADDING} from "home/CreateFlow.styles";
import {radii} from "theme/tokens";
import {outlinedControl} from "./outlinedControl.styles";
import {CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX} from "home/landing/campaignSpace";

/** Styles for {@link ./AddHero}, kept beside its markup per the repo's styles convention. */

/**
 * The dashed drop surface. A `div` since ui#1314, NOT a button: it now holds the two equal picker
 * controls the owner asked for, and a button inside a button is invalid markup the browser unnests.
 * The surface is still the visible drop target; activation belongs to the two real buttons, which is
 * also what gives a keyboard user two separately named stops instead of one control named after both.
 */
export const Hero = styled.div`
	width: 100%;
	/* As short as the consent takeover allows (owner decisions 2026-09-25, kitecorp/transferify-ui#2888:
	   the empty drop area was nearly as tall as the whole plate). The buttons and the hint need 95px to
	   147px. The floor is NOT them: the terms gate a first-time visitor meets fills this same card
	   (terms/Consent.styles), and above 760px its longest copy, German, needs a 232px card, both in the
	   stacked layout's 404px card and in the 360px one beside the campaign. 202px here makes a 240px
	   card: that plus 8px for font metrics on the Linux runners (measured at 1280, 768 and 320 in all three
	   languages). The padding term hands the
	   card's own inset back, as before. */
	min-height: calc(202px + 2 * (${CREATE_CARD_BLOCK_PADDING} - ${EMPTY_CREATE_CARD_PADDING}));

	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 9px;
	padding: 30px 16px 26px;
	border: 1.5px dashed ${({theme}) => theme.tokens.blue};
	background: ${({theme}) => theme.tokens.accentTint};
	/* One step below the card's 20px: an inner corner sitting 12px inside the outer one needs the
	   smaller radius to look concentric — equal radii pinched at the corners (owner, 2026-09-05). */
	border-radius: ${radii.panel};
	font-family: inherit;
	transition: box-shadow 180ms ease;

	&:hover {
		box-shadow: inset 0 0 0 1px ${({theme}) => theme.tokens.ring};
	}

	/* Unchanged at 760px and below: the German terms gate needs a 256px card there, and this gives 262px. */
	@media (max-width: 760px) {
		min-height: calc(224px + 2 * (${CREATE_CARD_BLOCK_PADDING} - ${EMPTY_CREATE_CARD_PADDING}));
	}
`;

/** The stacked pair's width: German's longer label (204px) with room either side. */
const STACKED_PICKER_WIDTH_PX = 232;

/**
 * The two picker buttons on one row. `wrap` rather than a media query: which width forces a second
 * line depends on how long the two labels are in the rendered locale ("Füge deine Dateien hinzu" is
 * twice the English), so the browser decides and no viewport gets a row wider than the card.
 *
 * Beside the campaign the card is `createCard.sideBySideWidthPx` wide, where the Romanian (292px) and
 * German (375px) pairs no longer fit on one row of 283px, so there the two stand one above the other on
 * purpose in every language, at one shared width (owner decision 2026-09-25,
 * kitecorp/transferify-ui#2888), rather than wrapping into two rows of different lengths.
 */
export const Controls = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: center;
	gap: 8px;
	max-width: 100%;

	@media (min-width: ${CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX}px) {
		flex-direction: column;
		flex-wrap: nowrap;
		align-items: stretch;
		width: min(100%, ${STACKED_PICKER_WIDTH_PX}px);
	}
`;


/**
 * One picker control. Both buttons are equal by construction: same element, same styles (ui#1314).
 * The frame is the shared {@link outlinedControl} the add-more pair under the file list also wears
 * (owner 2026-09-15), so the choice reads the same in the empty card and the filled one; only size
 * and type are set here.
 */
export const AddControl = styled.button`
	${outlinedControl}
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0 18px;
	border-radius: ${radii.panel};
	font-size: 14px;
	font-weight: 600;
	line-height: 1.2;
`;

export const Sub = styled.span`
	font-size: 12.5px;
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * The limit-wall arrival's hint (kitecorp/transferify-ui#3197): the drop hint's size, in the card's own
 * ink and bold, because it is an instruction the sender came back to follow rather than a reminder
 * that the whole page accepts drops. Centred, since it also shows in the stacked layout's narrow card.
 */
export const ArrivalHint = styled(Sub)`
	color: ${({theme}) => theme.tokens.ink};
	font-weight: 600;
	text-align: center;
`;
